import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Bell, Calendar, AlertTriangle, Wrench, Megaphone, Lock } from "lucide-react";

// Synthesized "ding" chime using WebAudio — no external asset needed.
function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = [
      { f: 880, t: 0.0 },
      { f: 1318.5, t: 0.12 },
    ];
    notes.forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.28, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.55);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.6);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch {}
}

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const prevCountRef = useRef(null);
  const initializedRef = useRef(false);
  const dropRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      const next = data.count || 0;
      const prev = prevCountRef.current;
      if (initializedRef.current && prev !== null && next > prev) {
        playChime();
        setPulse(true);
        setTimeout(() => setPulse(false), 4000);
      }
      prevCountRef.current = next;
      initializedRef.current = true;
      setCount(next);
    } catch {}
  }, []);

  const loadItems = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data);
    } catch {}
  };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 10000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    if (open) { loadItems(); setPulse(false); }
  }, [open]);

  useEffect(() => {
    const onDoc = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("click", onDoc);
    return () => window.removeEventListener("click", onDoc);
  }, []);

  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems((it) => it.map((x) => x.id === id ? { ...x, read: true } : x));
    loadCount();
  };
  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems((it) => it.map((x) => ({ ...x, read: true })));
    setCount(0);
    prevCountRef.current = 0;
  };

  const iconFor = (kind) => {
    if (kind === "reminder") return <Calendar className="w-4 h-4" />;
    if (kind === "critical") return <AlertTriangle className="w-4 h-4" />;
    if (kind === "work_order") return <Wrench className="w-4 h-4" />;
    if (kind === "driver_report") return <Megaphone className="w-4 h-4" />;
    if (kind === "period_lock" || kind === "period_unlock") return <Lock className="w-4 h-4" />;
    return <Bell className="w-4 h-4" />;
  };
  const tileFor = (kind) => {
    if (kind === "reminder")      return { bg: "rgba(242,201,76,0.14)", fg: "#F2C94C" };
    if (kind === "critical")      return { bg: "rgba(255,92,92,0.14)",  fg: "#FF5C5C" };
    if (kind === "work_order")    return { bg: "rgba(61,214,140,0.14)", fg: "#3DD68C" };
    if (kind === "driver_report") return { bg: "rgba(242,201,76,0.14)", fg: "#F2C94C" };
    if (kind === "period_lock" || kind === "period_unlock")
                                  return { bg: "rgba(155,163,174,0.14)", fg: "#9BA3AE" };
    return                          { bg: "rgba(255,122,26,0.14)", fg: "#FF7A1A" };
  };

  return (
    <div className="relative" ref={dropRef}>
      <button
        data-testid="notification-bell"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`relative w-10 h-10 rounded-md flex items-center justify-center transition-colors ${pulse ? "notif-shake" : ""}`}
        style={{
          background: open ? "var(--y-surface-2)" : "transparent",
          border: "1px solid " + (open ? "var(--y-border)" : "transparent"),
        }}
        aria-label="Bildirimler"
      >
        <Bell
          className={`w-5 h-5 ${pulse ? "notif-pulse" : ""}`}
          style={{ color: pulse || open ? "var(--y-accent)" : "var(--y-text-2)" }}
        />
        {count > 0 && (
          <>
            {pulse && (
              <span
                className="absolute top-0 right-0.5 w-[22px] h-[22px] rounded-full notif-ping"
                style={{ background: "var(--y-accent)", opacity: 0.7 }}
                aria-hidden
              />
            )}
            <span
              data-testid="notification-badge"
              className={`absolute top-1 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${pulse ? "notif-pulse" : ""}`}
              style={{
                background: "var(--y-danger)",
                color: "var(--y-accent-fg)",
                border: "1px solid var(--y-sidebar)",
              }}
            >
              {count > 9 ? "9+" : count}
            </span>
          </>
        )}
      </button>

      {open && (
        <div
          className="y-notif-panel"
          onClick={(e) => e.stopPropagation()}
          data-testid="notifications-panel"
        >
          <div
            className="px-4 py-3 flex items-center justify-between"
            style={{ borderBottom: "1px solid var(--y-border)", background: "var(--y-surface-2)" }}
          >
            <div>
              <div className="mono" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--y-label)" }}>
                Bildirimler
              </div>
              <div style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontSize: 14, fontWeight: 600, color: "var(--y-text)" }}>
                {count > 0 ? `${count} okunmamış` : "Tüm bildirimler okundu"}
              </div>
            </div>
            {items.some((i) => !i.read) && (
              <button
                onClick={markAll}
                className="text-xs font-medium hover:underline"
                style={{ color: "var(--y-accent)" }}
                data-testid="mark-all-read-btn"
              >
                Tümünü okundu işaretle
              </button>
            )}
          </div>
          <div
            className="drawer-scroll"
            style={{
              maxHeight: "min(50vh, 480px)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {items.length === 0 && (
              <div className="text-center py-10 text-sm" style={{ color: "var(--y-muted)" }}>
                Henüz bildirim yok
              </div>
            )}
            {items.map((n) => {
              const t = tileFor(n.kind);
              return (
                <div
                  key={n.id}
                  onClick={() => !n.read && markOne(n.id)}
                  className="flex gap-3 px-4 py-3 cursor-pointer transition-colors"
                  style={{
                    borderBottom: "1px solid var(--y-border)",
                    background: !n.read ? "var(--y-surface-2)" : "transparent",
                  }}
                  data-testid={`notif-${n.id}`}
                >
                  {!n.read && (
                    <span
                      className="shrink-0 self-center rounded-full"
                      style={{ width: 6, height: 6, background: "var(--y-accent)" }}
                      aria-hidden
                    />
                  )}
                  <div
                    className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center"
                    style={{ background: t.bg, color: t.fg }}
                  >
                    {iconFor(n.kind)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      style={{
                        fontFamily: "'Barlow Semi Condensed', sans-serif",
                        fontSize: 14,
                        fontWeight: !n.read ? 700 : 500,
                        color: "var(--y-text)",
                      }}
                    >
                      {n.title}
                    </div>
                    <div className="mt-0.5 line-clamp-2" style={{ fontSize: 12, color: "var(--y-muted)" }}>
                      {n.body}
                    </div>
                    <div className="mt-1 mono" style={{ fontSize: 10, color: "var(--y-label)" }}>
                      {new Date(n.created_at).toLocaleString("tr-TR")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
